import React, { Component } from 'react';
import GoogleMap from 'components/GoogleMap';
import Marker from 'components/GoogleMap/Marker';
import Button from 'components/deprecated/legacyElements/Button';
import H1 from 'components/deprecated/legacyElements/H1';
import H2 from 'components/deprecated/legacyElements/H2';
import styles from './style.module.scss';

function getNextMonday(hours = 8) {
  const nextMonday = new Date();
  const dayOfMonth =
    ((nextMonday.getDate() + (7 - nextMonday.getDay())) % 7) + 1;
  // if the day is before today, next monday is next month!
  if (dayOfMonth < nextMonday.getDate()) {
    nextMonday.setMonth(nextMonday.getMonth() + 1);
  }
  nextMonday.setDate(dayOfMonth);
  nextMonday.setHours(hours);
  return nextMonday;
}

// Cache the service itself (loaded async by maps API)
let distanceMatrixService;

// https://developers.google.com/maps/documentation/distance-matrix/intro
function distanceMatrixRequest(options = {}) {
  return new Promise((resolve, reject) => {
    if (
      !options.origins ||
      !options.destinations ||
      !options.origins.length ||
      !options.destinations.length
    ) {
      reject(new Error('Missing origin or destination'));
    } else {
      const nextMonday = getNextMonday();
      const parameters = {
        travelMode: 'DRIVING',
        transitOptions: {
          departureTime: nextMonday,
          routingPreference: 'LESS_WALKING',
        },
        drivingOptions: {
          departureTime: nextMonday,
          trafficModel: 'pessimistic',
        },
        unitSystem: 1,
        avoidHighways: false,
        avoidTolls: false,
        ...options,
      };

      if (!distanceMatrixService) {
        distanceMatrixService = new window.google.maps.DistanceMatrixService();
      }
      distanceMatrixService.getDistanceMatrix(parameters, (res, status) => {
        if (status !== window.google.maps.DistanceMatrixStatus.OK) {
          reject(status);
        } else {
          resolve(res);
        }
      });
    }
  });
}

function avg(arr) {
  return arr.reduce((sum, cur) => sum + cur, 0) / (arr.length || 1);
}

function readableSeconds(totalSecs, includeSeconds = false) {
  const hours = Math.floor(totalSecs / (60 * 60));
  const mins = Math.floor((totalSecs % (60 * 60)) / 60);
  const secs = Math.ceil(((totalSecs % (60 * 60)) % 60) * 100) / 100;
  const parts = [];
  if (hours > 0) parts.push(`${hours} hours`);
  if (hours > 0 || mins > 0 || !includeSeconds) parts.push(`${mins} minutes`);
  if (includeSeconds) parts.push(`${secs} secs`);
  return parts.join(', ');
}

function metersToMiles(meters) {
  return Math.ceil((meters * 100) / 1609.34) / 100;
}

function hexLerp(start, end, pos) {
  // convert start/end hex to rgb
  /* eslint-disable no-bitwise */
  const sI = parseInt(start.replace(/^#/, ''), 16);
  const eI = parseInt(end.replace(/^#/, ''), 16);
  const sR = sI >> 16;
  const sG = (sI >> 8) & 0xff;
  const sB = sI & 0xff;
  const eR = eI >> 16;
  const eG = (eI >> 8) & 0xff;
  const eB = eI & 0xff;
  const r = sR + pos * (eR - sR);
  const g = sG + pos * (eG - sG);
  const b = sB + pos * (eB - sB);
  return (
    '#' + (((1 << 24) + (r << 16) + (g << 8) + b) | 0).toString(16).slice(1)
  );
}

const SQUAREFOOT_HQ = { lat: 40.741047, lng: -73.992917 };

export default class CommuteCalculatorPage extends Component {
  state = {
    destination: SQUAREFOOT_HQ,
    positions: [],

    distances: [],
    durations: [],
    durationsInTraffic: [],

    travelMode: 'DRIVING',
  };

  updateDistances = () =>
    distanceMatrixRequest({
      origins: this.state.positions,
      destinations: [this.state.destination],
      travelMode: this.state.travelMode,
    })
      .then((result) => {
        const dataRows = result.rows
          .map((row) => row.elements[0])
          .filter((row) => row.status === 'OK');
        this.setState((prevState) => ({
          distances: dataRows.map((row) => row.distance.value),
          durations: dataRows.map((row) => row.duration.value),
          durationsInTraffic:
            prevState.travelMode === 'DRIVING'
              ? dataRows.map((row) => row.duration_in_traffic.value)
              : [],
        }));
      })
      .catch(() =>
        this.setState({
          distances: [],
          durations: [],
          durationsInTraffic: [],
        })
      );

  changeTravelMode = (evt) =>
    this.setState(
      {
        travelMode: evt.target.value,
      },
      this.updateDistances
    );

  clearMap = () =>
    this.setState(
      {
        positions: [],
      },
      this.updateDistances
    );

  onMapClick = (evt) => {
    this.setState(
      (prevState) => ({
        positions: prevState.positions.concat([evt.latLng.toJSON()]),
      }),
      this.updateDistances
    );
  };

  onMapRightClick = (evt) =>
    this.setState(
      {
        destination: evt.latLng.toJSON(),
      },
      this.updateDistances
    );

  onMarkerClick = (evt) => {
    // find the clicked marker in state.positions and remove it
    const latLng = evt.latLng.toJSON();
    const index = this.state.positions.findIndex(
      (pos) => pos.lat === latLng.lat && pos.lng === latLng.lng
    );
    this.setState(
      (prevState) => ({
        positions: prevState.positions
          .slice(0, index)
          .concat(prevState.positions.slice(index + 1)),
      }),
      this.updateDistances
    );
  };

  render() {
    const durationGoodness = Math.min(
      1,
      (avg(this.state.durations) - 900) / 1800
    ); // 15 - 30 mins
    const durationInTrafficGoodness = Math.min(
      1,
      (avg(this.state.durationsInTraffic) - 1200) / 2400
    ); // 20 - 40 mins
    const durationColor = hexLerp('#22d19b', '#f4502b', durationGoodness);
    const durationInTrafficColor = hexLerp(
      '#22d19b',
      '#f4502b',
      durationInTrafficGoodness
    );

    const markers = [
      <Marker
        position={this.state.destination}
        onClick={this.onMarkerClick}
        key={`${this.state.destination.lat}${this.state.destination.lng}`}
      >
        <img alt="Office Space" src={require('assets/images/brand-mark.svg')} />
      </Marker>,
    ].concat(
      this.state.positions.map((pos) => (
        <Marker
          position={pos}
          onClick={this.onMarkerClick}
          key={`${pos.lat}${pos.lng}`}
        />
      ))
    );

    return (
      <div className={`inset ${styles.container}`}>
        <H1 inset>Commute Calculator</H1>
        <div className={styles.row}>
          <article className={styles.header}>
            <H2>
              Find an office space that works for <em>everyone</em>
            </H2>
            <p>
              Click or tap on the map where your employees live to get estimated
              commute times.
            </p>
            <p>
              To move the destination, right click or long-press where
              you&rsquo;d like to go.
            </p>
          </article>
          <ul className={styles.mapControls}>
            <li>
              <label htmlFor="travelMode">
                Travel Mode:
                <div className="select">
                  <select
                    name="travelMode"
                    value={this.state.travelMode}
                    id="travelMode"
                    onChange={this.changeTravelMode}
                  >
                    <option value="DRIVING">
                      {String.fromCodePoint(0x1f697)} Driving
                    </option>
                    <option value="TRANSIT">
                      {String.fromCodePoint(0x1f686)} Public Transit
                    </option>
                    <option value="BICYCLING">
                      {String.fromCodePoint(0x1f6b4)} Bicycling
                    </option>
                    <option value="WALKING">
                      {String.fromCodePoint(0x1f6b6)} Walking
                    </option>
                  </select>
                </div>
              </label>
            </li>
            {this.state.distances.length > 0 && (
              <li>
                Average distance:&nbsp;
                <span>{metersToMiles(avg(this.state.distances))} miles</span>
              </li>
            )}
            {this.state.durations.length > 0 && (
              <li>
                Average time
                {this.state.durationsInTraffic.length > 0
                  ? ' without traffic: '
                  : ': '}
                <span style={{ color: durationColor }}>
                  {readableSeconds(avg(this.state.durations))}
                </span>
              </li>
            )}
            {this.state.durationsInTraffic.length > 0 && (
              <li>
                Average time <strong>with</strong> traffic:&nbsp;
                <span style={{ color: durationInTrafficColor }}>
                  {readableSeconds(avg(this.state.durationsInTraffic))}
                </span>
              </li>
            )}
            <li>
              <Button onClick={this.clearMap}>Clear Map</Button>
            </li>
          </ul>
        </div>
        <GoogleMap
          className={styles.map}
          options={{
            center: SQUAREFOOT_HQ,
            zoom: 12,
          }}
          onClick={this.onMapClick}
          onRightClick={this.onMapRightClick}
        >
          {markers}
        </GoogleMap>
      </div>
    );
  }
}
